import application from "@homer.core/application";
import { name } from "../package.json";

const pending = Symbol.for(`${name}:pending`);
const resolved = Symbol.for(`${name}:resolved`);
const value = Symbol.for(`${name}:value`);
const cache = new Map();
const timeoutDefault = 30000;

function resolveValue(node, resolver) {
    if (node[resolved]) {
        return Promise.resolve(node[value]);
    }

    const promise =
        typeof resolver === "function"
            ? new Promise((resolve, reject) => {
                  let result;

                  try {
                      result = resolver(resolve, reject);
                  } catch (error) {
                      reject(error);

                      return;
                  }

                  if (result !== undefined) {
                      Promise.resolve(result).then(resolve, reject);
                  }
              })
            : Promise.resolve(node);

    return promise.then((result) => {
        node[resolved] = true;
        node[value] = result;

        return result;
    });
}

function createResolverError(errorPrefix, error) {
    return new Error(`[${errorPrefix}] failed (resolver: ${error.message})`);
}

class Script {
    constructor(src, { id, crossorigin = null, prefix = true, resolver = null, timeout = timeoutDefault } = {}) {
        if (!id) {
            throw new TypeError("option `id` is required");
        }

        // Keep DOM lookup stable even if src contains dynamic parameters.
        const scriptId = prefix ? `${name}:${id}` : id;
        const errorPrefix = `${name}:${id}`;

        if (cache.has(id)) {
            return cache.get(id);
        }

        const existing = document.getElementById(scriptId);

        if (existing && existing.dataset.state !== "error" && existing.dataset.state !== "timeout") {
            const promise =
                existing[pending] ||
                (existing.dataset.state === "loading"
                    ? new Promise((resolvePromise, rejectPromise) => {
                          let timeoutId = null;
                          let isSettled = false;

                          const settle = (callback, value, state, isRejected = false) => {
                              if (isSettled) {
                                  return;
                              }

                              isSettled = true;
                              clearTimeout(timeoutId);
                              existing.dataset.state = state;

                              if (isRejected) {
                                  delete existing[pending];
                                  cache.delete(id);
                              }

                              callback(value);
                          };

                          existing.addEventListener(
                              "load",
                              () => {
                                  resolveValue(existing, resolver).then(
                                      (value) => {
                                          settle(resolvePromise, value, "loaded");
                                      },
                                      (error) => {
                                          settle(rejectPromise, createResolverError(errorPrefix, error), "error", true);
                                      },
                                  );
                              },
                              { once: true },
                          );
                          existing.addEventListener(
                              "error",
                              (event) => {
                                  settle(rejectPromise, new Error(`[${errorPrefix}] failed (${event.type})`), "error", true);
                              },
                              { once: true },
                          );
                          existing.addEventListener(
                              "abort",
                              (event) => {
                                  settle(rejectPromise, new Error(`[${errorPrefix}] failed (${event.type})`), "error", true);
                              },
                              { once: true },
                          );

                          if (timeout) {
                              timeoutId = setTimeout(() => {
                                  settle(rejectPromise, new Error(`[${errorPrefix}] failed (timeout)`), "timeout", true);
                              }, timeout);
                          }
                      })
                    : resolveValue(existing, resolver).catch((error) => {
                          existing.dataset.state = "error";
                          cache.delete(id);

                          throw createResolverError(errorPrefix, error);
                      }));

            cache.set(id, promise);

            if (existing.dataset.state === "loading") {
                existing[pending] = promise;
            }

            return promise;
        }

        if (existing) {
            existing.remove();
        }

        const script = document.createElement("script");
        let timeoutId = null;
        let isSettled = false;
        let resolvePromise = null;
        let rejectPromise = null;

        const promise = new Promise((resolvePromiseArgument, rejectPromiseArgument) => {
            resolvePromise = resolvePromiseArgument;
            rejectPromise = rejectPromiseArgument;
        });

        const settle = (callback, result, state, isRejected = false) => {
            if (isSettled) {
                return;
            }

            isSettled = true;
            clearTimeout(timeoutId);
            script.onerror = null;
            script.onabort = null;
            script.onload = null;
            script.dataset.state = state;

            if (isRejected) {
                delete script[pending];
                cache.delete(id);
            } else {
                script[resolved] = true;
                script[value] = result;
            }

            callback(result);
        };

        script.id = scriptId;
        script.src = src;
        script.dataset.state = "loading";
        script.nonce = application.nonce; // CSP-MARK
        script[pending] = promise;
        cache.set(id, promise);

        if (crossorigin !== null) {
            script.setAttribute("crossorigin", crossorigin === true ? "" : crossorigin);
        }

        script.onerror = script.onabort = (event) => {
            settle(rejectPromise, new Error(`[${errorPrefix}] failed (${event.type})`), "error", true);
        };

        script.onload = () => {
            resolveValue(script, resolver).then(
                (result) => {
                    settle(resolvePromise, result, "loaded");
                },
                (error) => {
                    settle(rejectPromise, createResolverError(errorPrefix, error), "error", true);
                },
            );
        };

        if (timeout && !isSettled) {
            timeoutId = setTimeout(() => {
                settle(rejectPromise, new Error(`[${errorPrefix}] failed (timeout)`), "timeout", true);
            }, timeout);
        }

        if (!isSettled) {
            document.head.appendChild(script);
        }

        return promise;
    }
}

export default Script;
